import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import IconButton from '@mui/material/IconButton';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// Bundle the worker as a same-origin .js chunk so it satisfies the CSP script-src 'self'
// directive and doesn't depend on the server serving .mjs with a JavaScript MIME type
pdfjsLib.GlobalWorkerOptions.workerPort = new Worker(
    new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url),
    { type: 'module' }
);

function PdfFormPreview({ formData, previousFormData = null, setPreviousFormData = () => { }, setCurrentFormData = () => { } }) {
    const containerRef = useRef(null);
    const [pdfDoc, setPdfDoc] = useState(null);
    const [numPages, setNumPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [formFieldsData, setFormFieldsData] = useState({});
    const [pageInputValue, setPageInputValue] = useState(1);

    // Load PDF
    useEffect(() => {
        const loadPdf = async () => {
            if (formData?.data?.Base64) {
                try {
                    const base64Data = formData.data.Base64.includes('base64,')
                        ? formData.data.Base64.split('base64,')[1]
                        : formData.data.Base64;

                    const pdfData = atob(base64Data);
                    const pdfArray = new Uint8Array(pdfData.length);
                    for (let i = 0; i < pdfData.length; i++) {
                        pdfArray[i] = pdfData.charCodeAt(i);
                    }

                    const loadingTask = pdfjsLib.getDocument({ data: pdfArray });
                    const pdf = await loadingTask.promise;
                    setPdfDoc(pdf);
                    setNumPages(pdf.numPages);
                } catch (error) {
                }
            }
        };

        loadPdf();
    }, [formData]);

    // Render all pages
    useEffect(() => {
        const renderAllPages = async () => {
            if (!pdfDoc || !containerRef?.current) return;

            containerRef.current.innerHTML = '';

            // Calculate a consistent scale using the first page
            const firstPage = await pdfDoc.getPage(1);
            const firstViewport = firstPage.getViewport({ scale: 1 });
            const containerWidth = containerRef?.current?.clientWidth;
            const scale = containerWidth / firstViewport.width;

            for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
                const page = await pdfDoc.getPage(pageNum);
                const viewport = page.getViewport({ scale });

                const pageWrapper = document.createElement('div');
                pageWrapper.style.position = 'relative';
                pageWrapper.style.marginBottom = '20px';
                pageWrapper.classList.add('pdf-page-wrapper');

                const canvas = document.createElement('canvas');
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                pageWrapper.appendChild(canvas);

                const context = canvas.getContext('2d');
                await page.render({ canvasContext: context, viewport }).promise;

                // Annotation layer
                const annotationLayer = document.createElement('div');
                annotationLayer.className = 'annotationLayer';
                annotationLayer.style.position = 'absolute';
                annotationLayer.style.left = '0';
                annotationLayer.style.top = '0';
                annotationLayer.style.width = viewport.width + 'px';
                annotationLayer.style.height = viewport.height + 'px';

                const annotations = await page.getAnnotations();
                annotations.forEach(annotation => {
                    if (!annotation.fieldType) return;
                    const rect = viewport.convertToViewportRectangle(annotation.rect);
                    const element = createFormElement(annotation, rect);
                    if (element) annotationLayer.appendChild(element);
                });

                pageWrapper.appendChild(annotationLayer);
                containerRef?.current?.appendChild(pageWrapper);
            }
        };

        renderAllPages();

        let resizeTimeout;
        const handleResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                renderAllPages();
            }, 300); // Debounce resize by 300ms
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(resizeTimeout);
        };

    }, [pdfDoc]);

    useEffect(() => {
        const extractFormData = async () => {
            if (!pdfDoc) return;

            const allFormData = {};

            for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
                const page = await pdfDoc.getPage(pageNum);
                const annotations = await page.getAnnotations();

                annotations.forEach(annotation => {
                    if (annotation.fieldType) {
                        const fieldName = annotation.fieldName || `field_${annotation.id}`;

                        // Extract value based on field type
                        let value = null;

                        if (annotation.fieldType === 'Tx') {
                            // Text field
                            value = annotation.fieldValue || annotation.defaultValue || '';
                        } else if (annotation.fieldType === 'Btn') {
                            // Checkbox or Radio
                            if (annotation.exportValue) {
                                // Checkbox
                                value = annotation.fieldValue === annotation.exportValue ? annotation.exportValue : 'Off';
                            } else if (annotation.buttonValue) {
                                // Radio
                                value = annotation.fieldValue || annotation.buttonValue || '';
                            }
                        } else if (annotation.fieldType === 'Ch') {
                            // Dropdown
                            value = annotation.fieldValue || annotation.defaultValue || '';
                        }

                        allFormData[fieldName] = value;
                    }
                });
            }
            setPreviousFormData(JSON.stringify(allFormData));
            setCurrentFormData(JSON.stringify(allFormData));
        };
        if (!previousFormData)
            extractFormData();
    }, [pdfDoc]);

    useEffect(() => {
        const container = containerRef?.current;
        if (!container) return;

        const handleScroll = () => {
            const children = Array.from(container.children);
            const scrollTop = container.scrollTop;
            const containerHeight = container.clientHeight;

            for (let i = 0; i < children.length; i++) {
                const child = children[i];
                const rect = child.getBoundingClientRect();
                const containerRect = container.getBoundingClientRect();

                if (rect.top <= containerRect.top + containerHeight / 2 &&
                    rect.bottom >= containerRect.top + containerHeight / 2) {
                    setCurrentPage(i + 1);
                    break;
                }
            }
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [pdfDoc]);

    useEffect(() => {
        setPageInputValue(currentPage);
    }, [currentPage]);

    const createFormElement = (annotation, rect) => {
        const fieldName = annotation.fieldName || `field_${annotation.id}`;
        const [x1, y1, x2, y2] = rect;

        const wrapper = document.createElement('div');
        wrapper.style.position = 'absolute';
        wrapper.style.left = Math.min(x1, x2) + 'px';
        wrapper.style.top = Math.min(y1, y2) + 'px';
        wrapper.style.width = Math.abs(x2 - x1) + 'px';
        wrapper.style.height = Math.abs(y2 - y1) + 'px';

        let element;

        switch (annotation.fieldType) {
            case 'Tx': // Text field
                element = document.createElement('input');
                element.type = 'text';

                element.value =
                    formFieldsData[fieldName] ??
                    annotation.fieldValue ??
                    annotation.appearanceState ??
                    '';

                element.style.width = '100%';
                element.style.height = '100%';
                element.style.border = '1px solid #ccc';
                element.style.padding = '2px';
                element.style.fontSize = '12px';
                element.style.boxSizing = 'border-box';

                element.addEventListener('input', (e) => {
                    updateFormField(fieldName, e.target.value);
                });
                break;

            case 'Btn': {
                // Checkbox
                if (annotation.exportValue) {
                    element = document.createElement('input');
                    element.type = 'checkbox';

                    const isChecked =
                        annotation.appearanceState === annotation.exportValue ||
                        annotation.fieldValue === annotation.exportValue ||
                        formFieldsData[fieldName] === annotation.exportValue;

                    element.checked = isChecked;

                    element.style.width = '100%';
                    element.style.height = '100%';
                    element.style.cursor = 'pointer';

                    element.addEventListener('change', e => {
                        updateFormField(fieldName, e.target.checked ? annotation.exportValue : "Off");
                    });
                }
                // Radio Button
                else if (annotation.buttonValue) {
                    element = document.createElement('input');
                    element.type = 'radio';
                    element.name = annotation.fieldName;
                    element.value = annotation.buttonValue;

                    // Try multiple value sources because PDFs differ
                    const radioValue =
                        annotation.fieldValue ??
                        annotation.appearanceState ??
                        annotation.buttonValue ??
                        annotation.defaultValue;

                    element.checked =
                        radioValue === annotation.buttonValue ||
                        radioValue === annotation.exportValue ||
                        formFieldsData[fieldName] === element.value;

                    element.style.width = '100%';
                    element.style.height = '100%';
                    element.style.cursor = 'pointer';

                    element.addEventListener('change', e => {
                        if (e.target.checked) {
                            updateFormField(fieldName, element.value);

                            // uncheck other radios in same group
                            const annotationLayer = e.target.closest('.annotationLayer');
                            if (annotationLayer) {
                                const radios = annotationLayer.querySelectorAll(`input[name="${annotation.fieldName}"]`);
                                radios.forEach(r => {
                                    if (r !== e.target) r.checked = false;
                                });
                            }
                        }
                    });
                }

                break;
            }

            case 'Ch': // Dropdown
                element = document.createElement('select');
                element.style.width = '100%';
                element.style.height = '100%';
                element.style.fontSize = '12px';

                if (annotation.options) {
                    annotation.options.forEach(option => {
                        const optElement = document.createElement('option');
                        optElement.value = option.exportValue || option.displayValue;
                        optElement.textContent = option.displayValue;
                        element.appendChild(optElement);
                    });
                }

                element.value =
                    formFieldsData[fieldName] ??
                    annotation.fieldValue ??
                    annotation.appearanceState ??
                    '';

                element.addEventListener('change', (e) => {
                    updateFormField(fieldName, e.target.value);
                });
                break;

            default:
                return null;
        }

        if (element) {
            wrapper.appendChild(element);
            return wrapper;
        }

        return null;
    };


    // Update form field value
    const updateFormField = (fieldName, value) => {
        setFormFieldsData(prev => {
            const updated = { ...prev, [fieldName]: value };
            setCurrentFormData(JSON.stringify(updated));
            return updated;
        });
    };

    // --- Pagination Scroll ---
    const goToPage = (page, e) => {
        // Prevent default if this was triggered from a click event
        if (e && typeof e.preventDefault === 'function') e.preventDefault();

        if (page < 1 || page > numPages || !containerRef.current) {
            return;
        }

        setCurrentPage(page);
        setPageInputValue(page);

        const container = containerRef.current;
        const children = Array.from(container.children);
        const targetPage = children[page - 1];

        if (targetPage) {
            // Calculate cumulative height of all previous pages
            let scrollPosition = 0;
            for (let i = 0; i < page - 1; i++) {
                scrollPosition += children[i].offsetHeight;
                // Account for margin-bottom (20px as set in your render function)
                scrollPosition += 20;
            }

            container.scrollTo({ top: scrollPosition, behavior: 'smooth' });
        }
    };


    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 80px)' }}>
            {/* Pagination UI */}
            <div style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "12px 16px",
                background: "#f5f5f5",
                borderBottom: "1px solid #e0e0e0",
                justifyContent: "space-between"
            }}>
                <div>
                    <IconButton onClick={(e) => goToPage(1, e)} disabled={currentPage === 1} size="small">
                        <FirstPageIcon />
                    </IconButton>
                    <IconButton onClick={(e) => goToPage(currentPage - 1, e)} disabled={currentPage === 1} size="small">
                        <ChevronLeftIcon />
                    </IconButton>
                    <IconButton onClick={(e) => goToPage(currentPage + 1, e)} disabled={currentPage === numPages} size="small">
                        <ChevronRightIcon />
                    </IconButton>
                    <IconButton onClick={(e) => goToPage(numPages, e)} disabled={currentPage === numPages} size="small">
                        <LastPageIcon />
                    </IconButton>
                </div>
                <div>
                    <input
                        type="number"
                        value={pageInputValue}
                        min={1}
                        max={numPages}
                        style={{
                            width: "50px",
                            textAlign: "center",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            padding: "4px",
                            fontSize: "14px"
                        }}
                        onChange={(e) => {
                            const value = Number(e.target.value);
                            setPageInputValue(e.target.value);

                            if (value >= 1 && value <= numPages) {
                                goToPage(value, e);
                            }
                        }}
                        onBlur={(e) => {
                            const value = Number(e.target.value);
                            if (value >= 1 && value <= numPages) {
                                goToPage(value, e);
                            } else {
                                setPageInputValue(currentPage);
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.target.blur();
                            }
                        }}
                    />

                    <span style={{ fontSize: "14px", color: "#666" }}>of {numPages}</span>
                </div>
            </div>

            {/* PDF Pages */}
            <div style={{
                flex: 1,
                overflow: 'auto',
                backgroundColor: '#525659',
                padding: '20px'
            }} ref={containerRef} />
        </div>
    );
}

export default PdfFormPreview;